import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api";
import {
  clientsApi,
  type ClientSummaryDto,
} from "@/lib/clients-accounts";
import { useAuth } from "@/app/auth-context";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Zod schema for "New client" drawer
// ---------------------------------------------------------------------------

const createSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  emailAddress: z.string().email().or(z.literal("")).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  mobilePhone: z.string().max(40).nullable().optional(),
  companyId: z.number().int().positive().nullable().optional(),
  companyName: z.string().max(200).nullable().optional(),
  departmentName: z.string().max(100).nullable().optional(),
  branch: z.string().max(50).nullable().optional(),
});
type CreateForm = z.infer<typeof createSchema>;

interface BranchDto { id: number; branchName: string; branchPrefix: string | null; }

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function ClientsListPage() {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState<string>("");
  const [hasCreditAccount, setHasCreditAccount] = useState<string>("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [creating, setCreating] = useState(false);

  const { me } = useAuth();
  const isAdmin = me?.roles.includes("Admin") ?? false;
  const qc = useQueryClient();

  // Build query string from filters
  const qs = [
    `page=${page}&pageSize=50`,
    q ? `q=${encodeURIComponent(q)}` : "",
    branch ? `branch=${encodeURIComponent(branch)}` : "",
    hasCreditAccount === "yes" ? "hasCreditAccount=true" : hasCreditAccount === "no" ? "hasCreditAccount=false" : "",
    !includeDeleted ? "deleted=false" : "",
  ]
    .filter(Boolean)
    .join("&");

  const { data, isLoading } = useQuery({
    queryKey: ["clients", page, q, branch, hasCreditAccount, includeDeleted],
    queryFn: () => clientsApi.list(qs),
  });

  const { data: branches } = useQuery({
    queryKey: ["branches-list"],
    queryFn: () => apiFetch<{ items: BranchDto[] }>("/api/branches?pageSize=200"),
    staleTime: 60_000,
  });

  const createMut = useMutation({
    mutationFn: (values: CreateForm) =>
      clientsApi.create({
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.emailAddress ?? null,
        phone: values.phone ?? null,
        mobilePhone: values.mobilePhone ?? null,
        companyId: values.companyId ?? null,
        companyName: values.companyName ?? null,
        departmentName: values.departmentName ?? null,
        branch: values.branch ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      setCreating(false);
    },
  });

  const cols: DataGridColumn<ClientSummaryDto>[] = [
    {
      id: "name",
      header: "Name",
      accessor: (r) => (
        <Link to={`/clients/${r.clientId}`} className="underline text-primary">
          {r.fullName}
        </Link>
      ),
    },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    { id: "email", header: "Email", accessor: (r) => r.emailAddress ?? "—" },
    { id: "phone", header: "Phone", accessor: (r) => r.phone ?? "—" },
    { id: "branch", header: "Branch", accessor: (r) => r.branch ?? "—" },
    {
      id: "credit",
      header: "Has credit",
      accessor: (r) => (r.hasApprovedCreditAccount ? "✓" : "—"),
    },
  ];

  return (
    <ListPage
      title="Clients"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}>New client</IntentButton>
      }
      filterBar={
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] grow">
            <Label htmlFor="client-q">Search</Label>
            <Input
              id="client-q"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder="Name, email, company…"
            />
          </div>
          <div className="min-w-[160px]">
            <Label htmlFor="client-branch">Branch</Label>
            <Select value={branch} onValueChange={(v) => { setBranch(v === "__all__" ? "" : v); setPage(0); }}>
              <SelectTrigger id="client-branch">
                <SelectValue placeholder="All branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All branches</SelectItem>
                {(branches?.items ?? []).map((b) => (
                  <SelectItem key={b.id} value={b.branchName}>{b.branchName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <Label htmlFor="client-credit">Has credit</Label>
            <Select value={hasCreditAccount} onValueChange={(v) => { setHasCreditAccount(v === "__all__" ? "" : v); setPage(0); }}>
              <SelectTrigger id="client-credit">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Switch id="client-deleted" checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
              <Label htmlFor="client-deleted">Show deleted</Label>
            </div>
          )}
        </div>
      }
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.clientId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />
      <DetailsDrawer
        open={creating}
        onOpenChange={(o) => { if (!o) setCreating(false); }}
        title="New client"
      >
        <EntityForm
          schema={createSchema}
          defaultValues={{
            firstName: "",
            lastName: "",
            emailAddress: "",
            phone: "",
            mobilePhone: "",
            companyId: null,
            companyName: "",
            departmentName: "",
            branch: "",
          }}
          onSubmit={(v) => createMut.mutate(v)}
        >
          {(f) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="c-fn">First name *</Label>
                <Input id="c-fn" {...f.register("firstName")} />
              </div>
              <div>
                <Label htmlFor="c-ln">Last name *</Label>
                <Input id="c-ln" {...f.register("lastName")} />
              </div>
              <div>
                <Label htmlFor="c-em">Email</Label>
                <Input id="c-em" type="email" {...f.register("emailAddress")} />
              </div>
              <div>
                <Label htmlFor="c-ph">Phone</Label>
                <Input id="c-ph" {...f.register("phone")} />
              </div>
              <div>
                <Label htmlFor="c-mp">Mobile</Label>
                <Input id="c-mp" {...f.register("mobilePhone")} />
              </div>
              <div>
                <Label htmlFor="c-co">Company name</Label>
                <Input id="c-co" {...f.register("companyName")} />
              </div>
              <div>
                <Label htmlFor="c-dn">Department</Label>
                <Input id="c-dn" {...f.register("departmentName")} />
              </div>
              <div>
                <Label htmlFor="c-br">Branch</Label>
                <Input id="c-br" {...f.register("branch")} />
              </div>
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? "Creating…" : "Create client"}
              </Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
